import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import alertService from '../../services/alertService';
import { playNotificationSound } from '../../utils/audioUtils';

// Based on services/backend/src/entities/alert.entity.ts
export interface AlertData {
  id: string;
  title: string;
  description: string | null;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'character' | 'item' | 'system' | 'market';
  acknowledged: boolean;
  metadata: Record<string, any> | null;
  game: 'PoE1' | 'PoE2';
  league: string;
  characterId?: string | null; // Assuming API might return characterId
  itemId?: string | null; // Assuming API might return itemId
  // If API returns populated character/item objects, define them here
  // character?: { id: string; name: string; /* other relevant fields */ };
  // item?: { id: string; name: string; /* other relevant fields */ };
  createdAt: string;
  updatedAt: string;
}

export interface Alert extends AlertData {
  // Potentially add frontend-specific transformations or hydrated objects here if needed
  // For now, Alert can be the same as AlertData if no transformation is done in the thunk
  // SOP deep-link fields (populated by backend when AlertSopLinks feature is on and a mapping exists)
  sopUrl?: string;
  sopLabel?: string | null;
}

// Removed placeholder alertServicePlaceholder

export type AlertAnimationState = 'entering' | 'stable' | 'exiting' | 'new-flash';

export interface Alert extends AlertData {
  // id is already in AlertData
  // createdAt is already in AlertData
  updatedAt: string; // Assuming BaseEntity provides updatedAt
  // character and item might be just IDs or simple objects if not fully populated
  character?: { id: string; name: string }; // Example, adjust as needed
  item?: { id: string; name: string }; // Example, adjust as needed
  // Animation state for smooth transitions
  animationState?: AlertAnimationState;
  isNew?: boolean;
}

interface AlertsState {
  alerts: Alert[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null | Record<string, any>;
  lastFetchTime: number | null;
  previousAlertIds: string[];
  newAlertIds: string[];
  exitingAlertIds: string[]; // Track alerts that are being removed
}

const initialState: AlertsState = {
  alerts: [],
  status: 'idle',
  error: null,
  lastFetchTime: null,
  previousAlertIds: [],
  newAlertIds: [],
  exitingAlertIds: [],
};

export const fetchAlerts = createAsyncThunk<Alert[], void, { state: RootState; rejectValue: string }>(
  'alerts/fetchAlerts',
  async (_, thunkAPI) => {
    try {
      // Get selected game and league from filter state
      const state = thunkAPI.getState();
      const selectedGame = state.filter.selectedGame;
      const selectedLeague = state.filter.selectedLeague;

      // Pass game and league to the API call
      const response = await alertService.getAlerts(selectedGame, selectedLeague);

      // Transform the response if needed
      return response.map((alert: AlertData) => ({
        ...alert,
        // Example transformation if needed:
        // character: alert.characterId ? { id: alert.characterId, name: 'Unknown Character' } : undefined,
        // item: alert.itemId ? { id: alert.itemId, name: 'Unknown Item' } : undefined,
      })) as Alert[];
    } catch (error: any) {
      const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  },
);

const alertsSlice = createSlice({
  name: 'alerts',
  initialState,
  reducers: {
    clearAlertsError: state => {
      state.error = null;
      state.status = 'idle';
    },
    forceRefresh: state => {
      state.status = 'idle';
    },
    clearNewAlerts: state => {
      state.newAlertIds = [];
      state.alerts = state.alerts.map(alert => ({
        ...alert,
        isNew: false,
        animationState: 'stable' as AlertAnimationState,
      }));
    },
    markAlertAsExiting: (state, action: PayloadAction<string>) => {
      const alertId = action.payload;
      state.exitingAlertIds.push(alertId);
      const alert = state.alerts.find(a => a.id === alertId);
      if (alert) {
        alert.animationState = 'exiting';
      }
    },
    removeExitingAlert: (state, action: PayloadAction<string>) => {
      const alertId = action.payload;
      state.alerts = state.alerts.filter(alert => alert.id !== alertId);
      state.exitingAlertIds = state.exitingAlertIds.filter(id => id !== alertId);
    },
    stabilizeNewAlerts: state => {
      state.alerts = state.alerts.map(alert => ({
        ...alert,
        animationState: alert.animationState === 'entering' ? ('new-flash' as AlertAnimationState) : alert.animationState,
      }));
    },
    completeNewAlertFlash: state => {
      state.alerts = state.alerts.map(alert => ({
        ...alert,
        animationState: alert.animationState === 'new-flash' ? ('stable' as AlertAnimationState) : alert.animationState,
        isNew: false,
      }));
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAlerts.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchAlerts.fulfilled, (state, action: PayloadAction<Alert[]>) => {
        state.status = 'succeeded';
        const incomingAlerts = action.payload;
        const incomingAlertIds = incomingAlerts.map(alert => alert.id);
        const currentAlertIds = state.alerts.map(alert => alert.id);

        // Identify different types of changes
        const newAlertIds = incomingAlertIds.filter(id => !currentAlertIds.includes(id));
        const resolvedAlertIds = currentAlertIds.filter(id => !incomingAlertIds.includes(id));
        const existingAlertIds = incomingAlertIds.filter(id => currentAlertIds.includes(id));

        // Handle resolved alerts - mark them for exit animation
        resolvedAlertIds.forEach(alertId => {
          const alert = state.alerts.find(a => a.id === alertId);
          if (alert && alert.animationState !== 'exiting') {
            alert.animationState = 'exiting';
            if (!state.exitingAlertIds.includes(alertId)) {
              state.exitingAlertIds.push(alertId);
            }
          }
        });

        // Handle new alerts - add them with entering animation state
        const newAlerts = incomingAlerts
          .filter(alert => newAlertIds.includes(alert.id))
          .map(alert => ({
            ...alert,
            animationState: 'entering' as AlertAnimationState,
            isNew: true,
          }));

        // Handle existing alerts - update their data but preserve animation state
        const updatedExistingAlerts = state.alerts
          .filter(alert => existingAlertIds.includes(alert.id))
          .map(existingAlert => {
            const incomingAlert = incomingAlerts.find(a => a.id === existingAlert.id);
            return incomingAlert
              ? {
                  ...incomingAlert,
                  animationState: existingAlert.animationState === 'exiting' ? 'exiting' : ('stable' as AlertAnimationState),
                  isNew: existingAlert.isNew || false,
                }
              : existingAlert;
          });

        // Handle exiting alerts - keep them temporarily for animation
        const exitingAlerts = state.alerts.filter(alert => resolvedAlertIds.includes(alert.id) && alert.animationState === 'exiting');

        // Combine all alerts
        state.alerts = [...updatedExistingAlerts, ...newAlerts, ...exitingAlerts];

        // Play notification sound for new critical alerts
        if (newAlertIds.length > 0) {
          const newCriticalAlerts = newAlerts.filter(alert => alert.severity.toLowerCase() === 'critical');
          if (newCriticalAlerts.length > 0) {
            playNotificationSound().catch(console.warn);
          }
          // Store new alert IDs for visual indication
          state.newAlertIds = [...state.newAlertIds, ...newAlertIds];
        }

        state.previousAlertIds = incomingAlertIds;
        state.lastFetchTime = Date.now();
      })
      .addCase(fetchAlerts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string | Record<string, any>;
      });
  },
});

export const {
  clearAlertsError,
  forceRefresh,
  clearNewAlerts,
  markAlertAsExiting,
  removeExitingAlert,
  stabilizeNewAlerts,
  completeNewAlertFlash,
} = alertsSlice.actions;

export const selectAllAlerts = (state: RootState) => state.alerts.alerts;
export const selectAlertsStatus = (state: RootState) => state.alerts.status;
export const selectAlertsError = (state: RootState) => state.alerts.error;
export const selectNewAlertIds = (state: RootState) => state.alerts.newAlertIds;
export const selectAlertsLastFetchTime = (state: RootState) => state.alerts.lastFetchTime;

export default alertsSlice.reducer;
